/**
 * Every magic number the audio subsystem owns. ARCHITECTURE.md §4.11.
 *
 * Numbers marked [MEASURED] were taken off the real Crazy Taxi arcade longplay
 * (reference/crazy-taxi/ct-arcade-longplay.mp4) with an FFT written for the purpose — see
 * docs/report-audio.md §"Reference measurements". They are not recalled, they are sampled.
 *
 * Frequencies are Hz, times are SECONDS unless the name ends in Ms, gains are linear 0..1.
 */

/** Deep-freeze so a typo like `T.engine.f0Max = 9` throws in strict mode instead of drifting. */
function deepFreeze(o) {
  for (const k of Object.keys(o)) {
    const v = o[k];
    if (v && typeof v === 'object' && !ArrayBuffer.isView(v)) deepFreeze(v);
  }
  return Object.freeze(o);
}

/** Equal-tempered midi → Hz. A4 (midi 69) = 440. */
export const mtof = (m) => 440 * Math.pow(2, (m - 69) / 12);

export const AUDIO_TUNING = deepFreeze({

  // ------------------------------------------------------------------ global

  /** Offline render rate for every pre-baked one-shot. Fixed so buffers are machine-independent. */
  renderRate: 44100,

  master: {
    /** Overall output trim. The whole game sits under this. */
    gain: 0.55,
    /**
     * [MEASURED] The reference mix puts only ~1.5 % of its energy above 4 kHz and
     * essentially nothing above 8 kHz. A gentle master lowpass reproduces that Dreamcast-era
     * midrange-forward balance and stops the synthesised noise layers sounding like hiss.
     */
    toneLpfHz: 9800,
    toneLpfQ: 0.5,
    /**
     * Subsonic trim. Nothing in the game is meant to be below the engine's 27 Hz idle
     * fundamental, and infrasonic noise-filter leakage was measured eating a large share of
     * the limiter's headroom. Gentle and well below anything audible.
     */
    toneHpHz: 26, toneHpQ: 0.6,
    /** A brick on the top only — the low end is deliberately untouched. */
    limiter: { threshold: -6, knee: 5, ratio: 20, attack: 0.003, release: 0.13 },
    /** Fade the whole graph in over this when the context first starts, to kill the boot click. */
    bootFade: 0.35,
  },

  /** Submix trims. These are the mix balance; change these first when something is too loud. */
  bus: {
    engine: 0.12,
    tyres: 0.10,
    sfx: 0.16,
    voice: 0.20,
    music: 0.75,
    ambience: 0.08,
    /**
     * Per-bus highpasses. Broad noise layers through low-Q bandpasses leak a lot of energy
     * an octave or two below their centre; measured, that leakage was 45–64 % of the tyre
     * and ambience beds' total power and it is pure mud. Null = no filter.
     */
    hp: { tyres: 70, ambience: 80, engine: null, sfx: null, voice: null, music: null },
  },

  duck: {
    /** How far the bed (music + ambience + engine + tyres) drops when the announcer speaks. */
    voiceAmount: 0.12,
    voiceAttack: 0.045,
    voiceRelease: 0.32,
    /** Tail held after the last syllable before the bed comes back. */
    voiceTail: 0.10,
    /** ctx.audio.duck(ms, amount) defaults. */
    defaultMs: 350,
    defaultAmount: 0.2,
  },

  // ------------------------------------------------------------------ engine

  /**
   * A works truck, not a sports car. The firing frequency of a 6-cylinder 4-stroke is
   * rpm/60 × cyl/2 = rpm/20, so a 620 rpm idle sits at 31 Hz and the 4200 rpm ceiling at
   * 210 Hz. That whole range lives under 250 Hz, which is exactly why it reads as heavy.
   * [MEASURED] the reference engine's audible content sits between 56 and 300 Hz.
   */
  engine: {
    idleRpm: 620,
    maxRpm: 4200,
    /** rpm → firing frequency divisor. 20 = inline six, four-stroke. */
    rpmPerHz: 20,
    /** Clamp so a wheelspin rpm spike cannot make it sound like a superbike. */
    f0Min: 27,
    f0Max: 215,

    /** Oscillator stack. Detune is in cents. */
    order1: { type: 'sawtooth', mul: 1.0, gain: 0.52, detune: 0 },
    order2: { type: 'square', mul: 0.5, gain: 0.40, detune: -7 },   // the lug: half order
    order3: { type: 'sawtooth', mul: 2.0, gain: 0.20, detune: 11 },
    sub: { type: 'sine', mul: 0.5, gain: 0.40, detune: 0 },

    /** Combustion/intake noise. Tracks load far more than rpm. */
    roar: {
      bandMul: 4.0, bandMin: 220, bandMax: 1500, q: 0.9,
      gainIdle: 0.07, gainLoad: 0.44,
    },

    /** Turbo whine — only really audible past `whineFrom` normalised rpm. */
    turbo: {
      hzAtIdle: 900, hzAtMax: 5200,
      whineFrom: 0.42,
      gain: 0.13,
      /** Second, detuned partial so it shimmers rather than sitting still. */
      detune: 26,
    },

    /** Off-throttle: low rumble comes UP when you lift at speed. */
    offThrottle: { lpHz: 190, q: 1.1, gain: 0.30 },

    /** Amplitude modulation at crank rate — the audible diesel lug. Fades out with rpm. */
    lug: { rateDiv: 3.0, depthIdle: 0.26, depthMax: 0.05, minHz: 6, maxHz: 70 },

    /** Master engine lowpass: cutoff climbs with rpm so revving opens the sound up. */
    lpf: { base: 330, perRpm01: 2600, q: 1.15 },

    /** Overall engine level curve. Idle must be present but not loud. */
    gainIdle: 0.38, gainMax: 1.0,

    /** Smoothing on the incoming rpm/load, in units per second. Stops zipper noise. */
    rpmSmooth: 14.0,
    loadSmooth: 8.0,
    /** AudioParam ramp constant. Long enough to be click-free, short enough to feel connected. */
    paramGlide: 0.035,

    /** Airborne: revs free, load drops, filter opens. */
    airborneLoad: 0.15,

    /** Gear-change chuff volume. */
    shiftGain: 0.45,
  },

  // ------------------------------------------------------------------ tyres

  tyres: {
    /** Speed (m/s) at which rolling noise reaches full level. */
    fullSpeed: 24,
    rollGain: 0.55,
    /** Rolling noise band centre climbs with speed: reads as "faster", not just "louder". */
    rollHzBase: 240, rollHzPerSpeed: 26, rollQ: 0.75,
    /** A second, lower rumble layer gives the road weight. */
    rumbleHz: 95, rumbleQ: 1.5, rumbleGain: 0.42,

    /** Screech. slipAngle (rad) above `slipFrom` starts it; `slipFull` is maximum. */
    slipFrom: 0.13, slipFull: 0.62,
    screechGain: 0.70,
    screechHz: 1180, screechQ: 8.5,
    /** Second resonance an octave-ish up so it bites. */
    screechHz2: 2460, screechQ2: 11.0, screechGain2: 0.45,
    /** Screech pitch rises a little with speed — a locked wheel at 80 sings higher. */
    screechHzPerSpeed: 9,

    /** Per-surface character. `hiss` is a bright wet layer, `chuff` is slush displacement. */
    surface: {
      road: { roll: 1.00, hiss: 0.00, chuff: 0.00, hp: 200, screech: 1.00 },
      sidewalk: { roll: 0.75, hiss: 0.00, chuff: 0.00, hp: 300, screech: 0.55 },
      kerb: { roll: 0.90, hiss: 0.00, chuff: 0.10, hp: 260, screech: 0.70 },
      grass: { roll: 0.55, hiss: 0.00, chuff: 0.45, hp: 160, screech: 0.15 },
      wet: { roll: 0.95, hiss: 0.85, chuff: 0.10, hp: 240, screech: 0.55 },
      slush: { roll: 0.70, hiss: 0.35, chuff: 1.00, hp: 180, screech: 0.30 },
      snow: { roll: 0.55, hiss: 0.10, chuff: 0.80, hp: 150, screech: 0.20 },
    },
    /** Weather forces a wet/slush overlay on top of whatever surface the wheel reports. */
    weatherOverlay: { thaw: 'road', nightrain: 'wet', winter: 'slush' },
    /** How strongly the weather overlay is blended in (0 = ignore weather). */
    weatherBlend: 0.7,

    hissHz: 4200, hissQ: 0.7, hissGain: 0.32,
    chuffHz: 620, chuffQ: 1.4, chuffGain: 0.55,
    /** Slush chuff is amplitude-modulated at wheel rate so it splatters rather than hisses. */
    chuffAmHz: 11, chuffAmDepth: 0.55,

    paramGlide: 0.05,
    surfaceSmooth: 6.0,
  },

  // ------------------------------------------------------------------ repair spray

  /**
   * The PowerWash layer. The single most important property: PITCH RISES WITH fill01, so the
   * meter is audible without looking at it. Three coupled things move with fill:
   * the noise band centre, a resonant peak, and a quiet tonal drone.
   */
  spray: {
    gain: 0.32,
    /**
     * Broadband hot-asphalt hiss. `bandMakeup` compensates for how little power a Q=1.25
     * bandpass passes: without it the fixed-frequency sizzle layer dominates the spectrum
     * and the pitch rise — the entire point of this sound — becomes inaudible. Measured
     * with src/audio/verify-offline.mjs: spectral centroid rise went 1.00x → 1.9x when this
     * was raised from 1.0 to 3.2 and the sizzle was cut.
     */
    bandHzAt0: 820, bandHzAt1: 3100, bandQ: 1.25, bandMakeup: 3.8,
    /** Resonant peak riding on top — this is what your ear tracks. */
    peakHzAt0: 1450, peakHzAt1: 4200, peakQ: 5.5, peakDb: 16,
    /** The tonal component. Low, so it reads as "material", not "whistle". */
    toneHzAt0: 172, toneHzAt1: 402, toneGain: 0.20, toneType: 'sawtooth',
    toneLpHzAt0: 500, toneLpHzAt1: 1400, toneLpQ: 3.0,
    /** Pump throb — a compressor cycling. */
    throbHz: 7.5, throbDepth: 0.22,
    /**
     * Sizzle: the hot bitumen hitting cold road. Band-limited on BOTH sides and scaled by
     * fill, so it adds texture late without flattening the spectral rise.
     */
    sizzleHpHz: 5200, sizzleLpHz: 9000, sizzleGain: 0.03, sizzleGainAt1: 0.07,
    /** Attack/release on the whole spray when the trigger goes down/up. */
    attack: 0.055, release: 0.16,
    /** Extra level as you close in on 100 % — the last 10 % should feel like effort. */
    lastPushFrom: 0.9, lastPushGain: 0.22,
    paramGlide: 0.06,
  },

  // ------------------------------------------------------------------ announcer

  /**
   * Formant synthesis. A sawtooth glottal source plus breath noise through four parallel
   * bandpass filters. It is not intelligible speech and is not meant to be — it is an arcade
   * BARK with the right rhythm and attitude, sitting under the UI's on-screen text.
   *
   * [MEASURED] the reference announcer's stable formant peaks were 657–678 Hz, 894 Hz and
   * 1700–1900 Hz over a 2 s bark, i.e. a back-vowel, male-register voice. `vowel.aw` and
   * `vowel.oh` below are pinned to those numbers.
   */
  announcer: {
    gain: 1.0,
    /** Base pitch of the voice in Hz. Individual lines multiply this. */
    basePitch: 116,
    /** Source mix. */
    buzzGain: 0.85, breathGain: 0.10,
    /** Formant filter Qs. Higher = more "robot", lower = more "human". Arcade wants tight. */
    formantQ: [9.0, 10.5, 11.0, 12.0],
    formantGain: [1.0, 0.72, 0.40, 0.20],
    /** Fixed 4th formant keeps the timbre consistent across vowels. */
    f4: 3300,
    /** Post chain: band-limit like a cabinet PA, then grit. */
    postHpHz: 230, postLpHz: 3900, driveAmount: 0.55,
    /** Vibrato on the glottal source. Small — it is a robot, not a crooner. */
    vibratoHz: 5.4, vibratoCents: 14,
    /** Per-syllable envelope. */
    attack: 0.014, release: 0.035,
    /** Silence inserted between syllables (the consonant gap). */
    gap: 0.028,
    /** Plosive burst on consonant-onset syllables. */
    plosive: { gain: 0.55, decay: 0.022, hp: 1400 },
    /** Sibilant ("s"/"sh") onset. */
    sibilant: { gain: 0.34, dur: 0.075, hp: 3200 },

    /** Queue behaviour. */
    maxQueue: 3,
    /** A queued line waits this long after the previous one ends. */
    queueGap: 0.09,
    /** A new line with priority strictly greater than the playing one cuts it off. */
    interruptFade: 0.05,
    /** Lines below this priority are dropped outright while something is speaking. */
    dropBelow: 0,

    /**
     * Peterson–Barney style formant triples (F1, F2, F3) in Hz.
     * `aw` and `oh` are the measured reference values.
     */
    vowels: {
      aa: [730, 1090, 2440],
      ae: [660, 1720, 2410],
      aw: [670, 900, 1780],   // [MEASURED] 657–678 / 894 / ~1766
      oh: [570, 860, 1750],   // [MEASURED] back-vowel neighbour of the above
      eh: [530, 1840, 2480],
      ee: [270, 2290, 3010],
      ih: [390, 1990, 2550],
      uh: [640, 1190, 2390],
      oo: [300, 870, 2240],
      er: [490, 1350, 1690],
    },

    /**
     * The bark table. Each syllable:
     *   v     vowel key
     *   d     duration, seconds
     *   p     pitch offset in semitones from the line's base
     *   bend  glide in semitones applied across the syllable
     *   g     level multiplier (stress)
     *   c     onset consonant: 'p' plosive, 's' sibilant, null none
     *
     * Rhythm matters far more than phonetics. Read them out loud with the durations and you
     * will hear the arcade cadence.
     */
    lines: {
      go: { pitch: 1.10, gain: 1.0, syl: [
        { v: 'ee', d: 0.13, p: 0, bend: 0, g: 0.9, c: 'p' },
        { v: 'ih', d: 0.11, p: 2, bend: 0, g: 0.85 },
        { v: 'oh', d: 0.34, p: 7, bend: 5, g: 1.0, c: 'p' },
      ] },
      combo: { pitch: 1.0, gain: 1.0, syl: [
        { v: 'aa', d: 0.17, p: 0, bend: 0, g: 1.0, c: 'p' },
        { v: 'oh', d: 0.22, p: 4, bend: 3, g: 0.95, c: 'p' },
      ] },
      perfect: { pitch: 1.18, gain: 1.0, syl: [
        { v: 'er', d: 0.20, p: 0, bend: 2, g: 1.0, c: 'p' },
        { v: 'eh', d: 0.15, p: 5, bend: 0, g: 0.8 },
        { v: 'ih', d: 0.24, p: 9, bend: 4, g: 1.0, c: 'p' },
      ] },
      timeExtend: { pitch: 1.05, gain: 0.95, syl: [
        { v: 'ae', d: 0.16, p: 0, bend: 0, g: 0.9, c: 'p' },
        { v: 'oh', d: 0.14, p: 3, bend: 0, g: 0.8 },
        { v: 'aa', d: 0.28, p: 7, bend: 3, g: 1.0 },
      ] },
      thump: { pitch: 0.82, gain: 1.0, syl: [
        { v: 'uh', d: 0.30, p: 0, bend: -7, g: 1.0 },
      ] },
      slalom: { pitch: 1.08, gain: 0.95, syl: [
        { v: 'aa', d: 0.15, p: 0, bend: 0, g: 0.95, c: 's' },
        { v: 'oh', d: 0.13, p: 3, bend: 0, g: 0.8 },
        { v: 'uh', d: 0.22, p: 8, bend: 3, g: 1.0 },
      ] },
      thread: { pitch: 1.14, gain: 0.85, syl: [
        { v: 'ih', d: 0.18, p: 0, bend: 5, g: 1.0, c: 's' },
      ] },
      timeWarning: { pitch: 1.22, gain: 1.0, syl: [
        { v: 'aa', d: 0.14, p: 0, bend: 0, g: 1.0, c: 'p' },
        { v: 'oh', d: 0.13, p: -2, bend: 0, g: 0.85 },
        { v: 'ee', d: 0.20, p: 5, bend: 4, g: 1.0 },
      ] },
      timeUp: { pitch: 0.95, gain: 1.0, syl: [
        { v: 'aa', d: 0.20, p: 0, bend: 0, g: 1.0, c: 'p' },
        { v: 'uh', d: 0.34, p: -5, bend: -4, g: 0.95, c: 'p' },
      ] },
      gameOver: { pitch: 0.86, gain: 1.0, syl: [
        { v: 'ae', d: 0.24, p: 0, bend: 0, g: 1.0, c: 'p' },
        { v: 'oh', d: 0.18, p: -2, bend: 0, g: 0.85 },
        { v: 'er', d: 0.42, p: -7, bend: -5, g: 0.95 },
      ] },
      comboLost: { pitch: 0.90, gain: 0.9, syl: [
        { v: 'aw', d: 0.20, p: 0, bend: -3, g: 1.0 },
        { v: 'oh', d: 0.28, p: -5, bend: -4, g: 0.9 },
      ] },
    },

    /** `combo<N>` maps to the `combo` line, pitched up per tier and capped. */
    comboTierSemitones: 1.6,
    comboTierMax: 8,

    /** line → alias. Anything not in `lines` and not here falls back to `combo`. */
    alias: {
      combo2: 'combo', combo3: 'combo', combo4: 'combo', combo5: 'combo',
      combo6: 'combo', combo7: 'combo', combo8: 'combo',
      gameover: 'gameOver', over: 'gameOver',
      timewarning: 'timeWarning', warn: 'timeWarning',
      timeup: 'timeUp', start: 'go', ready: 'go',
      nice: 'thread', bonk: 'thump', ouch: 'thump',
    },
  },

  // ------------------------------------------------------------------ music

  /**
   * Adaptive punk/ska loop. [MEASURED] the reference soundtrack runs 152–172 BPM depending
   * on the track; 168 sits in the middle of that and is a canonical punk tempo.
   *
   * The form is SIXTEEN bars with real chord movement, and four macro sections that rotate
   * on every pass, so a ten-minute session never repeats the same bar twice in a row.
   */
  music: {
    bpm: 168,
    /** Tempo pushes toward this at full intensity. Latched at bar boundaries only. */
    bpmHigh: 178,
    /** Scheduler lookahead, seconds. Must exceed one frame at 30 fps with margin. */
    lookahead: 0.18,

    /**
     * Chord roots as MIDI note numbers with an optional third.
     * A minor with a borrowed D major (the Dorian brightener) and an E major (the punk
     * dominant). [MEASURED] every root here appears in the reference bass spectrum:
     * C2 65.4→64.6, D2 73.4→72.7, E2 82.4→83.4, F2 87.3, G2 98→94.2, A2 110→110.4.
     */
    chords: {
      Am: { root: 45, third: 3 },
      F: { root: 41, third: 4 },
      C: { root: 36, third: 4 },
      G: { root: 43, third: 4 },
      E: { root: 40, third: 4 },
      D: { root: 38, third: 4 },
    },
    /** 16-bar form. One chord per bar. */
    form: ['Am', 'F', 'C', 'G', 'Am', 'F', 'C', 'E', 'D', 'D', 'F', 'G', 'F', 'G', 'Am', 'G'],

    /**
     * Macro sections, rotated per pass through the form. Patterns are 16 steps (16ths).
     * `1` = hit. Keeping them as strings makes the groove readable.
     */
    sections: [
      { // 0 — straight punk
        name: 'punk',
        kick: '1000001010000010',
        snare: '0000100000001000',
        hatC: '1010101010101010',
        hatO: '0000000000000010',
        guitar: '1010101010101010',
        gtrLen: 0.55,
        bass: '1010101010101010',
        crashBar0: true,
      },
      { // 1 — ska upstrokes, drums pull back
        name: 'ska',
        kick: '1000000010000000',
        snare: '0000100000001000',
        hatC: '0010001000100010',
        hatO: '0000000000000000',
        guitar: '0010001000100010',
        gtrLen: 0.14,
        bass: '1000101010001010',
        crashBar0: false,
      },
      { // 2 — driving, doubled
        name: 'drive',
        kick: '1000101010001010',
        snare: '0000100000001010',
        hatC: '1111111111111111',
        hatO: '0000000000001000',
        guitar: '1010101010101010',
        gtrLen: 0.45,
        bass: '1111101011111010',
        crashBar0: true,
      },
      { // 3 — half-time breakdown, then it hurts more when it comes back
        name: 'breakdown',
        kick: '1000000000100000',
        snare: '0000000010000000',
        hatC: '1000100010001000',
        hatO: '0000000000000000',
        guitar: '1000000000000000',
        gtrLen: 1.60,
        bass: '1000000010000000',
        crashBar0: true,
      },
    ],

    /**
     * Which layers are audible at which intensity. Below `from` the layer is silent; it
     * fades to full over `fade`.
     */
    layer: {
      kick: { from: 0.00, fade: 0.05, gain: 1.00 },
      bass: { from: 0.02, fade: 0.10, gain: 0.90 },
      hat: { from: 0.04, fade: 0.12, gain: 0.62 },
      snare: { from: 0.24, fade: 0.12, gain: 0.95 },
      guitar: { from: 0.34, fade: 0.18, gain: 1.00 },
      crash: { from: 0.50, fade: 0.10, gain: 0.70 },
      lead: { from: 0.68, fade: 0.16, gain: 0.45 },
    },
    /** Master music gain also scales with intensity so quiet moments really are quiet. */
    intensityGain: { min: 0.55, max: 1.0 },

    /** Bass synth. */
    bassSynth: { type: 'sawtooth', sub: 'square', subGain: 0.18, lpBase: 340, lpPerVel: 1500, q: 3.2, attack: 0.004, decay: 0.10, sustain: 0.55, release: 0.05 },
    /** Guitar power chord: root, fifth, octave, all detuned and distorted. */
    guitarSynth: { type: 'sawtooth', detuneCents: [-9, 0, 8], voicing: [12, 19, 24], drive: 0.62, lpBase: 3000, hp: 220, attack: 0.003, release: 0.05, q: 0.9 },
    /** Lead: a square-wave hook an octave above. */
    leadSynth: { type: 'square', lp: 3200, q: 2.0, attack: 0.005, release: 0.08, octave: 36 },
    /** Lead hook, semitone offsets from the chord root; -1 = rest. 16 steps per bar. */
    leadPattern: [0, -1, 7, -1, 12, -1, 7, -1, 10, -1, 7, -1, 5, -1, 3, -1],

    /** Humanisation, drawn once from the seeded fork at init. Milliseconds and gain. */
    swingMs: 7,
    velJitter: 0.13,
    timingJitterMs: 4,

    /** How long a manual music('intensity:x') call overrides the automatic value. */
    manualHoldS: 6.0,
    /** Automatic intensity weights. */
    auto: { base: 0.22, comboWeight: 0.44, comboFull: 8, timeWeight: 0.28, timeFrom: 22, speedWeight: 0.14, speedFull: 90 },
    /** Intensity slew, units per second. Music must not lurch. */
    intensitySlew: 0.45,
    fadeIn: 0.6,
    fadeOut: 0.9,
    /** Polyphony caps per role. */
    poly: { bass: 2, guitar: 4, lead: 2 },

    /**
     * Static trims per role, applied ONCE on the role's bus. The per-note velocity already
     * carries `layer.<x>.gain`; these exist only to set the kit's internal balance.
     */
    trim: { kick: 1.00, snare: 0.95, hat: 0.85, crash: 0.70, tom: 0.70, rim: 0.60, bass: 1.00, guitar: 1.00, lead: 1.00 },
    /** Per-note velocity scalars, before layer gain and humanisation. */
    hit: { kick: 0.95, snare: 0.90, hatAccent: 1.00, hatWeak: 0.62, bass: 0.50, guitar: 0.42, lead: 0.26 },
    /**
     * A 2nd-order highpass on the music bus. Without it the bass sub-octave and the kick
     * tail put 57 % of the music's energy below 60 Hz (measured), which buries the guitars
     * and eats all the headroom the limiter has.
     */
    busHpHz: 42, busHpQ: 0.7,
  },

  // ------------------------------------------------------------------ ambience

  ambience: {
    crossfade: 1.4,
    /** Distant city bed — the default 'thaw' daytime layer. */
    city: { lpHz: 900, q: 0.6, gain: 0.55, humHz: 58, humGain: 0.045, swellHz: 0.07, swellDepth: 0.35 },
    /** Wind — a wandering bandpass. */
    wind: { hzMin: 260, hzMax: 940, q: 1.1, gain: 0.75, wanderHz: 0.055, gustHz: 0.19, gustDepth: 0.45 },
    /** Rain — bright hiss plus a mid body. */
    rain: { hpHz: 1500, bodyHz: 700, bodyQ: 0.8, gain: 0.80, bodyGain: 0.5, patterHz: 3.1, patterDepth: 0.18 },
    /** Per-weather layer mix: [city, wind, rain]. */
    weather: {
      thaw: [1.00, 0.18, 0.00],
      winter: [0.35, 1.00, 0.00],
      nightrain: [0.45, 0.30, 1.00],
    },
  },

  // ------------------------------------------------------------------ sfx

  sfx: {
    /** Hard polyphony cap. Past this, the oldest quietest voice is stolen. */
    maxVoices: 24,
    /** The same key cannot retrigger faster than this. Stops machine-gunning. */
    minRetriggerMs: 26,
    /** Distance model: gain = 1 / (1 + d/ref), silent past max. */
    refDistance: 14,
    maxDistance: 130,
    /** Stereo width of positional sfx. */
    panWidth: 0.75,
    /** Doppler is deliberately absent — arcade sfx read better without it. */

    /**
     * key → { buf, gain, rate, jitter, pan }
     * `buf` names a bank entry. `jitter` is ± rate randomisation drawn from the seeded table.
     */
    keys: {
      // ---- gameplay calls these by exactly these names. Do not rename.
      thump: { buf: 'thump', gain: 1.00, rate: 1.00, jitter: 0.05 },
      coneHit: { buf: 'coneBonk', gain: 0.85, rate: 1.00, jitter: 0.14 },
      coneDeploy: { buf: 'conePlace', gain: 0.70, rate: 1.00, jitter: 0.10 },
      repairStart: { buf: 'sprayStart', gain: 0.80, rate: 1.00, jitter: 0.04 },
      repairMilestone: { buf: 'tick', gain: 0.72, rate: 1.00, jitter: 0.01 },
      repairComplete: { buf: 'sealThunk', gain: 0.85, rate: 1.00, jitter: 0.03 },
      patchCured: { buf: 'cureShimmer', gain: 0.55, rate: 1.00, jitter: 0.03 },
      patchRuined: { buf: 'ruin', gain: 0.80, rate: 1.00, jitter: 0.05 },
      slalomChain: { buf: 'chainBlip', gain: 0.62, rate: 1.00, jitter: 0.02 },
      slalomBreak: { buf: 'breakThud', gain: 0.60, rate: 1.00, jitter: 0.05 },
      timeWarning: { buf: 'warnBeep', gain: 0.75, rate: 1.00, jitter: 0.00 },
      'vehicle.impact': { buf: 'crunch', gain: 1.00, rate: 1.00, jitter: 0.10 },

      // ---- audio's own, fired from bus events and internal logic.
      chime: { buf: 'chime', gain: 0.90, rate: 1.00, jitter: 0.00 },
      chimePerfect: { buf: 'chimePerfect', gain: 1.00, rate: 1.00, jitter: 0.00 },
      timeBell: { buf: 'timeBell', gain: 0.80, rate: 1.00, jitter: 0.02 },
      sprayEnd: { buf: 'sprayEnd', gain: 0.60, rate: 1.00, jitter: 0.05 },
      landing: { buf: 'landing', gain: 0.90, rate: 1.00, jitter: 0.08 },
      gearShift: { buf: 'gearShift', gain: 0.45, rate: 1.00, jitter: 0.08 },
      splash: { buf: 'splash', gain: 0.70, rate: 1.00, jitter: 0.14 },
      whoosh: { buf: 'whoosh', gain: 0.65, rate: 1.00, jitter: 0.12 },
      horn: { buf: 'horn', gain: 0.85, rate: 1.00, jitter: 0.02 },
      crunch: { buf: 'crunch', gain: 1.00, rate: 1.00, jitter: 0.10 },
      coneBonk: { buf: 'coneBonk', gain: 0.85, rate: 1.00, jitter: 0.14 },
    },

    /** repair:completed with rate >= this is read as a PERFECT and gets the big chime. */
    perfectRateThreshold: 1.06,
  },

  // ------------------------------------------------------------------ event reactions

  events: {
    /** vehicle:landed → thud scaled by impact speed. */
    landImpactFull: 9.0,
    landMinSpeed: 1.6,
    /** traffic:nearMiss → whoosh; gap in metres at which it is loudest. */
    nearMissGap: 2.4,
    /** vehicle:collided impulse that maps to full-gain crunch. */
    collideImpulseFull: 14.0,
    /** pothole:thumped also punches the bed down briefly. It is the signature sound. */
    thumpDuckMs: 260, thumpDuckAmount: 0.18,
    /** repair:completed ducks the bed so the chime lands clean. */
    completeDuckMs: 420, completeDuckAmount: 0.15,
  },

  /** Diagnostic: dump every scheduled music step to the console. Never on by default. */
  debugMusic: false,
});

export default AUDIO_TUNING;
